import React, { useState, useRef } from 'react'
import { Upload, X, Image as ImageIcon, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import { supabase } from '@/lib/supabase'

interface ImageUploadProps {
  label: string
  description?: string
  currentImageUrl?: string | null
  onImageChange: (url: string | null) => void
  bucketName: string
  folderPath: string
  maxSizeInMB?: number
  acceptedTypes?: string[]
  className?: string
}

export function ImageUpload({
  label,
  description,
  currentImageUrl,
  onImageChange,
  bucketName,
  folderPath,
  maxSizeInMB = 5,
  acceptedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/svg+xml'],
  className = ''
}: ImageUploadProps) {
  const [isUploading, setIsUploading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    setError(null)

    // Validar tipo de arquivo
    if (!acceptedTypes.includes(file.type)) {
      setError(`Tipo de arquivo não suportado. Use: ${acceptedTypes.join(', ')}`)
      return
    }

    // Validar tamanho do arquivo
    const maxSizeInBytes = maxSizeInMB * 1024 * 1024
    if (file.size > maxSizeInBytes) {
      setError(`Arquivo muito grande. Tamanho máximo: ${maxSizeInMB}MB`)
      return
    }

    setIsUploading(true)

    if (!supabase) {
      throw new Error('Supabase não está disponível')
    }

    try {
      // Gerar nome único para o arquivo
      const fileExtension = file.name.split('.').pop()
      const fileName = `${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExtension}`
      const filePath = `${folderPath}/${fileName}`

      // Fazer upload do arquivo
      const { data, error: uploadError } = await supabase.storage
        .from(bucketName)
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        })

      if (uploadError) {
        throw new Error(`Erro no upload: ${uploadError.message}`)
      }

      // Obter URL pública do arquivo
      const { data: { publicUrl } } = supabase.storage
        .from(bucketName)
        .getPublicUrl(data.path)

      onImageChange(publicUrl)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro desconhecido no upload')
    } finally {
      setIsUploading(false)
      // Limpar o input
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    }
  }

  const handleRemoveImage = async () => {
    if (!currentImageUrl) return
    
    if (!supabase) {
      onImageChange(null)
      return
    }

    try {
      // Extrair o caminho do arquivo da URL
      const url = new URL(currentImageUrl)
      const pathParts = url.pathname.split('/')
      const bucketIndex = pathParts.findIndex(part => part === bucketName)
      
      if (bucketIndex !== -1 && bucketIndex < pathParts.length - 1) {
        const filePath = pathParts.slice(bucketIndex + 1).join('/')
        
        // Remover arquivo do storage
        await supabase.storage
          .from(bucketName)
          .remove([filePath])
      }
    } catch (err) {
      console.error('Erro ao remover arquivo do storage:', err)
    }

    onImageChange(null)
  }

  const triggerFileSelect = () => {
    fileInputRef.current?.click()
  }

  return (
    <div className={`space-y-2 ${className}`}>
      <Label>{label}</Label>
      {description && (
        <p className="text-sm text-muted-foreground">{description}</p>
      )}
      
      <Card className="border-dashed border-2 hover:border-primary/50 transition-colors">
        <CardContent className="p-6">
          {currentImageUrl ? (
            <div className="space-y-4">
              <div className="relative group">
                <img
                  src={currentImageUrl}
                  alt="Preview"
                  className="w-full h-32 object-contain rounded-md bg-muted"
                />
                <Button
                  type="button"
                  variant="destructive"
                  size="sm"
                  className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity"
                  onClick={handleRemoveImage}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
              <Button
                type="button"
                variant="outline"
                onClick={triggerFileSelect}
                disabled={isUploading}
                className="w-full"
              >
                {isUploading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Enviando...
                  </>
                ) : (
                  <>
                    <Upload className="mr-2 h-4 w-4" />
                    Alterar Imagem
                  </>
                )}
              </Button>
            </div>
          ) : (
            <div className="text-center">
              <ImageIcon className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
              <Button
                type="button"
                variant="outline"
                onClick={triggerFileSelect}
                disabled={isUploading}
                className="mb-2"
              >
                {isUploading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Enviando...
                  </>
                ) : (
                  <>
                    <Upload className="mr-2 h-4 w-4" />
                    Selecionar Imagem
                  </>
                )}
              </Button>
              <p className="text-sm text-muted-foreground">
                Clique para selecionar ou arraste uma imagem
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                Máximo {maxSizeInMB}MB • {acceptedTypes.map(type => type.split('/')[1]).join(', ')}
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {error && (
        <p className="text-sm text-destructive">{error}</p>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept={acceptedTypes.join(',')}
        onChange={handleFileSelect}
        className="hidden"
      />
    </div>
  )
}